/**
 * Budget Timeline — Scene-level cost estimation with bar chart and cost driver breakdown.
 */
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  DollarSign, TrendingDown, Calendar, AlertTriangle,
  ChevronDown, ChevronUp,
} from 'lucide-react';

const TIER_COLORS = {
  low: '#4CAF50',
  moderate: '#FFD700',
  high: '#FF8C42',
  very_high: '#DC143C',
};

const TIER_LABELS = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  very_high: 'Very High',
};

const DRIVER_LABELS = {
  location_complexity: 'Location Complexity',
  vfx: 'VFX',
  stunts: 'Stunts',
  crowd_extras: 'Crowd / Extras',
  special_equipment: 'Special Equipment',
  night_shoot: 'Night Shoot',
  wardrobe_makeup: 'Wardrobe & Makeup',
  animals_children: 'Animals / Children',
  post_production: 'Post Production',
};

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function SummaryCard({ icon: Icon, label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] px-4 py-3 flex-1 min-w-[140px]"
    >
      <Icon size={16} className={accent || 'text-[var(--text-muted)]'} />
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{label}</p>
        <p className="text-sm font-semibold text-[var(--text-primary)]">{value}</p>
      </div>
    </motion.div>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-3 text-xs shadow-lg max-w-[260px]">
      <p className="font-semibold text-[var(--text-primary)] mb-1">Scene {d.scene_number}</p>
      <p className="text-[var(--text-muted)] mb-2 italic">{d.slug_line}</p>
      <div className="flex items-center gap-2 mb-1">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: TIER_COLORS[d.cost_tier] }}
        />
        <span className="text-[var(--text-secondary)]">
          {TIER_LABELS[d.cost_tier]} &middot; Cost {d.relative_cost}/100
        </span>
      </div>
      <p className="text-[var(--text-muted)]">
        <Calendar size={10} className="inline mr-1" />
        {d.shooting_days} shooting day{d.shooting_days !== 1 ? 's' : ''}
      </p>
      {d.top_drivers?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-[var(--border-subtle)]">
          <p className="text-[var(--text-muted)] mb-1">Top drivers:</p>
          {d.top_drivers.map((dr, i) => (
            <p key={i} className="text-[var(--text-secondary)]">
              {DRIVER_LABELS[dr.category] || dr.category}: {dr.score}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function CostDriverBar({ category, score, note, maxScore }) {
  const width = maxScore > 0 ? (score / maxScore) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-32 text-[var(--text-muted)] truncate">
        {DRIVER_LABELS[category] || category}
      </span>
      <div className="flex-1 h-3 rounded-full bg-[var(--bg-app)] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{
            backgroundColor:
              score >= 60 ? '#DC143C' : score >= 30 ? '#FF8C42' : score >= 15 ? '#FFD700' : '#4CAF50',
          }}
        />
      </div>
      <span className="w-8 text-right text-[var(--text-secondary)] font-medium">{score}</span>
      {note && (
        <span className="text-[var(--text-muted)] text-[10px] truncate max-w-[160px]">{note}</span>
      )}
    </div>
  );
}

export default function BudgetTimeline({ data }) {
  const [selectedSceneIdx, setSelectedSceneIdx] = useState(null);
  const [showReductions, setShowReductions] = useState(true);

  const scenes = data?.scenes || [];
  const summary = data?.summary || {};
  const overall_producibility_score = data?.overall_producibility_score || 0;

  const chartData = useMemo(
    () =>
      scenes.map((s) => {
        const sorted = [...(s.cost_drivers || [])].sort((a, b) => b.score - a.score);
        return {
          ...s,
          name: `${s.scene_number}`,
          top_drivers: sorted.slice(0, 3),
        };
      }),
    [scenes],
  );

  const selectedScene =
    selectedSceneIdx !== null ? scenes[selectedSceneIdx] : null;
  const maxDriverScore = selectedScene
    ? Math.max(...(selectedScene.cost_drivers || []).map((d) => d.score), 1)
    : 1;

  if (!data?.scenes?.length) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Budget Analysis</h3>
        <span className={`text-lg font-bold ${scoreColor(overall_producibility_score)}`}>
          {overall_producibility_score}/100
        </span>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="flex flex-wrap gap-3">
          <SummaryCard
            icon={Calendar}
            label="Shooting Days"
            value={summary.estimated_shooting_days}
            accent="text-blue-400"
          />
          <SummaryCard
            icon={DollarSign}
            label="Budget Tier"
            value={summary.budget_tier}
            accent="text-amber-400"
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Budget Concentration"
            value={`${summary.budget_concentration}%`}
            accent="text-orange-400"
          />
          <SummaryCard
            icon={TrendingDown}
            label="Top Expensive Scenes"
            value={summary.top_5_expensive_scenes?.length || 0}
            accent="text-red-400"
          />
        </div>
      )}

      {/* Bar Chart */}
      <div className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4">
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
          Relative Cost per Scene
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 4, bottom: 4, left: 0 }}
              onClick={(state) => {
                if (state?.activeTooltipIndex !== undefined) {
                  const idx = state.activeTooltipIndex;
                  setSelectedSceneIdx(selectedSceneIdx === idx ? null : idx);
                }
              }}
            >
              <XAxis
                dataKey="name"
                tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
                axisLine={{ stroke: 'var(--border-subtle)' }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
                axisLine={false}
                tickLine={false}
                width={30}
              />
              <RechartsTooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Bar dataKey="relative_cost" radius={[3, 3, 0, 0]} cursor="pointer">
                {chartData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={TIER_COLORS[entry.cost_tier] || '#888'}
                    opacity={selectedSceneIdx !== null && selectedSceneIdx !== i ? 0.35 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tier Legend */}
        <div className="flex gap-4 mt-2 justify-center">
          {Object.entries(TIER_COLORS).map(([tier, color]) => (
            <span key={tier} className="flex items-center gap-1 text-[9px] text-[var(--text-muted)]">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              {TIER_LABELS[tier]}
            </span>
          ))}
        </div>
      </div>

      {/* Selected Scene Detail */}
      <AnimatePresence mode="wait">
        {selectedScene && (
          <motion.div
            key={selectedSceneIdx}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-primary)]">
                  Scene {selectedScene.scene_number}
                </h4>
                <p className="text-[10px] text-[var(--text-muted)] italic">
                  {selectedScene.slug_line}
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{
                    backgroundColor: `${TIER_COLORS[selectedScene.cost_tier]}20`,
                    color: TIER_COLORS[selectedScene.cost_tier],
                  }}
                >
                  {TIER_LABELS[selectedScene.cost_tier]}
                </span>
                <span className="text-[var(--text-muted)]">
                  <Calendar size={10} className="inline mr-1" />
                  {selectedScene.shooting_days} day{selectedScene.shooting_days !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Cost Drivers */}
            <div className="space-y-1.5">
              {(selectedScene.cost_drivers || [])
                .filter((d) => d.score > 0)
                .sort((a, b) => b.score - a.score)
                .map((driver, i) => (
                  <CostDriverBar
                    key={i}
                    category={driver.category}
                    score={driver.score}
                    note={driver.note}
                    maxScore={maxDriverScore}
                  />
                ))}
            </div>

            {/* Cost Reduction Suggestion */}
            {selectedScene.cost_reduction_suggestion && (
              <div className="mt-3 flex items-start gap-2 rounded-md bg-[var(--bg-app)] p-2 text-xs">
                <TrendingDown size={12} className="text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-[var(--text-secondary)]">
                  {selectedScene.cost_reduction_suggestion}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cost Reduction Opportunities */}
      {summary?.cost_reduction_opportunities?.length > 0 && (
        <div className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4">
          <button
            onClick={() => setShowReductions(!showReductions)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <TrendingDown size={14} className="text-emerald-400" />
              <span className="text-xs font-semibold text-[var(--text-primary)]">
                Cost Reduction Opportunities
              </span>
              <span className="text-[10px] text-[var(--text-muted)] ml-1">
                ({summary.cost_reduction_opportunities.length})
              </span>
            </div>
            {showReductions ? (
              <ChevronUp size={14} className="text-[var(--text-muted)]" />
            ) : (
              <ChevronDown size={14} className="text-[var(--text-muted)]" />
            )}
          </button>

          <AnimatePresence>
            {showReductions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-3 space-y-2">
                  {summary.cost_reduction_opportunities.map((opp, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3 rounded-md bg-[var(--bg-app)] p-2.5 text-xs"
                    >
                      <span className="shrink-0 text-[10px] font-mono font-medium text-amber-400 bg-amber-400/10 rounded px-1.5 py-0.5">
                        Sc {opp.scene}
                      </span>
                      <p className="flex-1 text-[var(--text-secondary)]">{opp.suggestion}</p>
                      {opp.savings_percentage != null && (
                        <span className="shrink-0 text-emerald-400 font-medium whitespace-nowrap">
                          -{opp.savings_percentage}%
                        </span>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
